/**
 * ETFHoldingsBenchmarkChart — full multi-benchmark / multi-constituent
 * comparison chart for an ETF and its holdings, rendered on a unified
 * percentage Y-axis so every series can be directly compared regardless of
 * absolute price.
 *
 * Features:
 *   • Toggle ETF, SPY, QQQ, DIA on/off
 *   • Toggle every constituent (up to 100+) on/off — first toggle on lazy-
 *     fetches that symbol's history so we don't fire 100 parallel HTTP calls
 *     on initial mount.
 *   • Toggle full indicator suite computed on the ETF series:
 *     Regression, VWAP, EMA(5/10/20/50/200), SMA(5/10/20/50/200), RSI,
 *     MFI, VIX, Volume, ATR(14), Support/Resistance, 1× ATR alert,
 *     2× ATR stop, Warning zone shaded band.
 *   • Auto Relative-Strength Ranker — leaders & laggards vs ETF for the
 *     selected window with one-click filters.
 *   • Percentage-based Y-axis (auto-expands to fit visible series).
 *
 * Props:
 *   etfTicker  – string  (parent ETF symbol)
 *   holdings   – array   (sorted by weight desc; objects must have .symbol)
 *   timeframe  – string  (1d|5d|10d|20d|50d|100d|200d|mtd|ytd|1y)
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from "recharts";
import { BarChart3, RefreshCw, Eye, EyeOff, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { API } from "../config/api";
import { enrichWithAnchoredVwap } from "../utils/vwap";

// Palette big enough for 120 holdings — colors recycle with light shifts
// once we wrap past 30 (browsers display them as distinct enough lines that
// the chart legend stays readable).
const PALETTE = [
  "#fb7185", "#fdba74", "#fde047", "#86efac", "#5eead4",
  "#93c5fd", "#a5b4fc", "#c4b5fd", "#f0abfc", "#fda4af",
  "#facc15", "#fb923c", "#84cc16", "#22c55e", "#14b8a6",
  "#06b6d4", "#0ea5e9", "#6366f1", "#8b5cf6", "#d946ef",
  "#ec4899", "#f43f5e", "#ef4444", "#10b981", "#f97316",
  "#eab308", "#65a30d", "#0d9488", "#0891b2", "#7c3aed",
];

const CORE_BENCHMARKS = [
  { key: "etf", label: "ETF",            color: "#a855f7", strokeWidth: 2.8, kind: "etf"   },
  { key: "spy", label: "SPY · S&P 500",  color: "#ffffff", strokeWidth: 2.0, kind: "bench" },
  { key: "qqq", label: "QQQ · Nasdaq",   color: "#22d3ee", strokeWidth: 2.0, kind: "bench" },
  { key: "dia", label: "DIA · Dow",      color: "#fbbf24", strokeWidth: 2.0, kind: "bench" },
];

// Indicators that read off the price-derived series — values are converted
// into the %-from-start axis before plotting.
const MA_CONFIG = [
  { key: "ema5",   label: "5 EMA",   period: 5,   kind: "ema", color: "#fb7185" },
  { key: "ema10",  label: "10 EMA",  period: 10,  kind: "ema", color: "#fdba74" },
  { key: "ema20",  label: "20 EMA",  period: 20,  kind: "ema", color: "#fde047" },
  { key: "ema50",  label: "50 EMA",  period: 50,  kind: "ema", color: "#86efac" },
  { key: "ema200", label: "200 EMA", period: 200, kind: "ema", color: "#5eead4" },
  { key: "sma5",   label: "5 SMA",   period: 5,   kind: "sma", color: "#93c5fd" },
  { key: "sma10",  label: "10 SMA",  period: 10,  kind: "sma", color: "#a5b4fc" },
  { key: "sma20",  label: "20 SMA",  period: 20,  kind: "sma", color: "#c4b5fd" },
  { key: "sma50",  label: "50 SMA",  period: 50,  kind: "sma", color: "#f0abfc" },
  { key: "sma200", label: "200 SMA", period: 200, kind: "sma", color: "#fda4af" },
];

const RIGHT_AXIS_INDICATORS = [
  { key: "rsi",    label: "RSI",     color: "#ef4444" },
  { key: "mfi",    label: "MFI",     color: "#22c55e" },
  { key: "vix",    label: "VIX",     color: "#fb923c", dasharray: "3 3" },
  { key: "volume", label: "Volume",  color: "#a78bfa" },
  { key: "atr",    label: "ATR(14)", color: "#0ea5e9", dasharray: "5 3" },
];

async function fetchSeries(symbol, timeframe) {
  try {
    const r = await axios.get(`${API}/stock/indicators-chart/${symbol}`, {
      params: { timeframe }, timeout: 35000,
    });
    return r.data?.chart_data || [];
  } catch (_e) { return []; }
}

const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
const pctClass = (v) => v == null ? "text-slate-300" : v >= 0 ? "text-emerald-300" : "text-rose-300";

export default function ETFHoldingsBenchmarkChart({ etfTicker, holdings, timeframe }) {
  // Track every holding (no cap) so the user can toggle the full universe.
  const trackedHoldings = useMemo(() => {
    if (!Array.isArray(holdings)) return [];
    return holdings
      .filter(h => h.symbol)
      .map((h, idx) => ({
        symbol: h.symbol,
        name: h.name,
        weight_pct: h.weight_pct,
        color: PALETTE[idx % PALETTE.length],
      }));
  }, [holdings]);

  // Per-symbol series data { etf:[], spy:[], qqq:[], dia:[], <SYM>:[] }
  const [seriesData, setSeriesData] = useState({});
  const [vixBars, setVixBars] = useState([]);
  const [loading, setLoading] = useState(false);
  // Symbols currently in-flight (so the UI can show a spinner per pill).
  const [pendingSymbols, setPendingSymbols] = useState(new Set());
  // Token to cancel stale fetches when the user changes timeframe/ticker
  const fetchTokenRef = useRef(0);

  // Visibility — ETF + SPY + top 5 holdings on; benchmarks/indicators off.
  const [visible, setVisible] = useState({});

  useEffect(() => {
    const init = {
      etf: true, spy: true, qqq: false, dia: false,
      regression: false, vwap: false,
      rsi: false, mfi: false, vix: false, volume: false, atr: false,
      zones: false, atr_alert: false, atr_stop: false, atr_zone: false,
      ...Object.fromEntries(MA_CONFIG.map(m => [m.key, false])),
    };
    trackedHoldings.forEach((h, i) => { init[h.symbol] = i < 5; });
    setVisible(init);
  // Reset whenever we get a different ETF or holdings list
  // eslint-disable-next-line
  }, [etfTicker, trackedHoldings.map(h => h.symbol).join(",")]);

  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));
  const setMany = (entries) => setVisible(v => ({ ...v, ...entries }));

  // Lazy-fetch a single holding's series.  Called when the user toggles on a
  // holding whose data we haven't loaded yet.  Stays cached in seriesData so
  // re-toggling is instant.
  const fetchHolding = useCallback(async (symbol, tf) => {
    if (!symbol) return;
    setPendingSymbols(prev => { const next = new Set(prev); next.add(symbol); return next; });
    const myToken = fetchTokenRef.current;
    const bars = await fetchSeries(symbol, tf);
    // Guard against stale results when timeframe/etf changed mid-flight
    if (myToken !== fetchTokenRef.current) return;
    setSeriesData(prev => ({ ...prev, [symbol]: bars }));
    setPendingSymbols(prev => { const next = new Set(prev); next.delete(symbol); return next; });
  }, []);

  // Initial bulk load: ETF + broad benchmarks + first 10 holdings + VIX.
  // The remaining holdings (10..N) are fetched only when the user toggles
  // them on or hits "Load all" — keeps mount fast even for 100-holding ETFs.
  const loadInitial = useCallback(async () => {
    if (!etfTicker || trackedHoldings.length === 0) return;
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setLoading(true);
    try {
      const top10 = trackedHoldings.slice(0, 10).map(h => h.symbol);
      const symbols = [
        ["etf", etfTicker],
        ["spy", "SPY"],
        ["qqq", "QQQ"],
        ["dia", "DIA"],
        ...top10.map(s => [s, s]),
      ];
      const vixPromise = axios
        .get(`${API}/stock/vix-chart/${etfTicker}`, { params: { timeframe }, timeout: 35000 })
        .then(r => r.data?.chart_data || [])
        .catch(() => []);

      const results = await Promise.all(
        symbols.map(async ([key, sym]) => [key, await fetchSeries(sym, timeframe)])
      );
      const vix = await vixPromise;
      if (myToken !== fetchTokenRef.current) return;
      setSeriesData(Object.fromEntries(results));
      setVixBars(vix);
    } finally {
      if (myToken === fetchTokenRef.current) setLoading(false);
    }
  }, [etfTicker, timeframe, trackedHoldings]);

  // Re-fetch initial bundle whenever the ETF/timeframe changes.  Clears the
  // cache so we don't show stale percentages from a different window.
  useEffect(() => {
    setSeriesData({});
    setVixBars([]);
    loadInitial();
  }, [loadInitial]);

  // Toggle wrapper that lazy-fetches a holding when toggled on for the
  // first time.  Benchmarks & indicators don't need this (already loaded).
  const toggleHolding = useCallback((symbol) => {
    setVisible(v => ({ ...v, [symbol]: !v[symbol] }));
    if (!seriesData[symbol]) {
      fetchHolding(symbol, timeframe);
    }
  }, [seriesData, fetchHolding, timeframe]);

  // Bulk "Show all holdings" — toggle every holding ON and lazy-fetch any
  // missing series.  Spawns the requests in parallel so a 100-holding ETF
  // finishes loading within seconds.
  const handleShowAllHoldings = useCallback(() => {
    const updates = {};
    const missing = [];
    trackedHoldings.forEach(h => {
      updates[h.symbol] = true;
      if (!seriesData[h.symbol]) missing.push(h.symbol);
    });
    setMany(updates);
    missing.forEach(sym => fetchHolding(sym, timeframe));
  }, [trackedHoldings, seriesData, fetchHolding, timeframe]);

  const handleHideAllHoldings = () => {
    const updates = {};
    trackedHoldings.forEach(h => { updates[h.symbol] = false; });
    setMany(updates);
  };

  // -------- Math layer: rebase every series to % change from start --------
  // The ETF defines the time axis. Every constituent / benchmark is mapped
  // by {time -> pct_from_start} so the chart can plot them on one axis.
  // ETF-derived indicators (EMA/SMA/VWAP/regression/ATR/support/resistance)
  // are computed in price space then converted to %-from-ETF-start.
  const computed = useMemo(() => {
    const etfBars = seriesData.etf || [];
    if (etfBars.length < 2) return { chartData: [], performance: {}, allKeys: [] };
    const etfPrices = etfBars.map(d => d.price).filter(p => p > 0);
    if (etfPrices.length < 2) return { chartData: [], performance: {}, allKeys: [] };
    const etfStart = etfPrices[0];
    const etfEnd   = etfPrices[etfPrices.length - 1];
    const etfPct   = (etfEnd / etfStart - 1) * 100;
    const round    = v => v == null ? null : Math.round(v * 100) / 100;
    const toPct    = (price) => price == null ? null
                              : Math.round((price / etfStart - 1) * 10000) / 100;

    const rebasePctLookup = (bars) => {
      if (!bars || bars.length < 2) return { map: {}, pct: null, start: null, end: null };
      const ps = bars.map(d => d.price).filter(p => p > 0);
      if (ps.length < 2) return { map: {}, pct: null, start: null, end: null };
      const start = ps[0];
      const end   = ps[ps.length - 1];
      const map = {};
      bars.forEach(d => {
        if (d.price > 0) map[d.time] = Math.round((d.price / start - 1) * 10000) / 100;
      });
      return { map, pct: (end / start - 1) * 100, start, end };
    };

    const keys = ["etf", "spy", "qqq", "dia", ...trackedHoldings.map(h => h.symbol)];
    const rebased = {};
    keys.forEach(k => { rebased[k] = rebasePctLookup(seriesData[k]); });

    // VIX values per time (raw 0-100 axis)
    const vixByTime = {};
    (vixBars || []).forEach(d => { if (d?.vix != null) vixByTime[d.time] = d.vix; });

    // ----- ETF-derived indicators (computed in price space) -----
    // EMAs / SMAs
    const computeSma = (arr, period) => {
      if (arr.length < period) return new Array(arr.length).fill(null);
      const out = new Array(arr.length).fill(null);
      let sum = 0;
      for (let i = 0; i < arr.length; i++) {
        sum += arr[i];
        if (i >= period) sum -= arr[i - period];
        if (i >= period - 1) out[i] = sum / period;
      }
      return out;
    };
    const computeEma = (arr, period) => {
      if (arr.length < period) return new Array(arr.length).fill(null);
      const k = 2 / (period + 1);
      const out = new Array(arr.length).fill(null);
      let seed = 0;
      for (let i = 0; i < period; i++) seed += arr[i];
      seed /= period;
      out[period - 1] = seed;
      for (let i = period; i < arr.length; i++) {
        out[i] = arr[i] * k + out[i - 1] * (1 - k);
      }
      return out;
    };
    const maSeries = {};
    MA_CONFIG.forEach(m => {
      maSeries[m.key] = m.kind === "ema"
        ? computeEma(etfPrices, m.period)
        : computeSma(etfPrices, m.period);
    });

    // Linear regression on ETF prices
    const n = etfPrices.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    etfPrices.forEach((p, i) => { sumX += i; sumY += p; sumXY += i*p; sumX2 += i*i; });
    const denom = n * sumX2 - sumX * sumX;
    const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;

    // VWAP enrichment on ETF bars
    const withVwap = enrichWithAnchoredVwap(etfBars);

    // Wilder ATR(14) — capped to bar count
    const CANONICAL_ATR_PERIOD = 14;
    const ATR_PERIOD = etfBars.length > CANONICAL_ATR_PERIOD
      ? CANONICAL_ATR_PERIOD
      : Math.max(2, etfBars.length - 1);
    const atrRaw = new Array(etfBars.length).fill(null);
    if (etfBars.length > ATR_PERIOD) {
      const trs = new Array(etfBars.length).fill(null);
      for (let i = 1; i < etfBars.length; i++) {
        const d = etfBars[i], prev = etfBars[i - 1];
        const h = d?.high ?? d?.price, lo = d?.low ?? d?.price;
        const pc = prev?.price;
        if (h == null || lo == null || pc == null) continue;
        trs[i] = Math.max(h - lo, Math.abs(h - pc), Math.abs(lo - pc));
      }
      let seed = 0, seeded = 0;
      for (let i = 1; i <= ATR_PERIOD && i < trs.length; i++) {
        if (trs[i] != null) { seed += trs[i]; seeded++; }
      }
      if (seeded >= Math.max(2, ATR_PERIOD - 1)) {
        atrRaw[ATR_PERIOD] = seed / seeded;
        for (let i = ATR_PERIOD + 1; i < trs.length; i++) {
          if (trs[i] == null || atrRaw[i - 1] == null) continue;
          atrRaw[i] = (atrRaw[i - 1] * (ATR_PERIOD - 1) + trs[i]) / ATR_PERIOD;
        }
      }
    }
    const atrValues = atrRaw.filter(v => v != null);
    const maxAtr   = atrValues.length ? Math.max(...atrValues) : 0;
    const atrScale = maxAtr > 0 ? 100 / maxAtr : 0;
    let lastAtrRaw = null;
    for (let i = atrRaw.length - 1; i >= 0; i--) {
      if (atrRaw[i] != null) { lastAtrRaw = atrRaw[i]; break; }
    }
    const atrStopPrice = lastAtrRaw != null ? etfEnd - 2 * lastAtrRaw : null;
    const atrAlertPrice = lastAtrRaw != null ? etfEnd - 1 * lastAtrRaw : null;
    const atrStopPct  = atrStopPrice  != null ? toPct(atrStopPrice)  : null;
    const atrAlertPct = atrAlertPrice != null ? toPct(atrAlertPrice) : null;

    // Volume normalization for right axis (0-100)
    const volumes = etfBars.map(d => (d?.volume != null ? d.volume : 0));
    const maxVol  = Math.max(...volumes, 0);
    const volScale = maxVol > 0 ? 100 / maxVol : 0;

    // Support / Resistance derived from min/max ETF price in the window
    const support     = Math.min(...etfPrices);
    const resistance  = Math.max(...etfPrices);
    const supportPct    = toPct(support);
    const resistancePct = toPct(resistance);

    // Build the merged rows
    let priceIdx = 0;
    const rows = withVwap.map((d, idx) => {
      const hasPrice = d.price > 0;
      const row = { time: d.time };
      // Holdings + benchmarks
      keys.forEach(k => { row[k] = rebased[k].map[d.time] ?? null; });
      // VWAP (price → %)
      row.vwap = d.vwap != null ? toPct(d.vwap) : null;
      // Regression (price → %)
      const reg = hasPrice ? intercept + slope * priceIdx : null;
      row.regression = reg != null ? toPct(reg) : null;
      // EMA/SMA (price → %)
      if (hasPrice) {
        MA_CONFIG.forEach(m => {
          const v = maSeries[m.key][priceIdx];
          row[m.key] = v != null ? toPct(v) : null;
        });
        priceIdx++;
      } else {
        MA_CONFIG.forEach(m => { row[m.key] = null; });
      }
      // Right-axis indicators
      row.rsi = d.rsi ?? null;
      row.mfi = d.mfi ?? null;
      row.vix = vixByTime[d.time] ?? null;
      row.volume     = d?.volume != null ? Math.round(d.volume * volScale * 100) / 100 : null;
      row.volume_raw = d?.volume ?? null;
      const atrR = atrRaw[idx];
      row.atr     = atrR != null ? Math.round(atrR * atrScale * 100) / 100 : null;
      row.atr_raw = atrR != null ? Math.round(atrR * 100) / 100 : null;
      return row;
    });

    const perf = {};
    keys.forEach(k => {
      const r = rebased[k];
      perf[k] = { pct: round(r.pct), start: r.start, end: r.end };
    });

    return {
      chartData: rows,
      performance: perf,
      allKeys: keys,
      etfPct: round(etfPct),
      etfStart, etfEnd,
      // Indicator outputs
      supportPct, resistancePct,
      atrPeriod: ATR_PERIOD,
      lastAtr: lastAtrRaw,
      atrStopPct, atrAlertPct,
      regressionStats: {
        slope: round(slope),
        startPct: toPct(intercept),
        endPct: toPct(intercept + slope * (n - 1)),
      },
    };
  }, [seriesData, vixBars, trackedHoldings]);

  const {
    chartData, performance, allKeys, etfPct,
    supportPct, resistancePct, atrPeriod, lastAtr,
    atrStopPct, atrAlertPct, regressionStats,
  } = computed;

  // Y-axis domain — expands to include every visible series + indicator.
  const yDomain = useMemo(() => {
    const candidates = [];
    const priceKeys = [...allKeys, "vwap", "regression", ...MA_CONFIG.map(m => m.key)];
    chartData.forEach(d => {
      priceKeys.forEach(k => {
        if (visible[k] && typeof d[k] === "number" && Number.isFinite(d[k])) {
          candidates.push(d[k]);
        }
      });
    });
    if (visible.zones && supportPct != null)    candidates.push(supportPct);
    if (visible.zones && resistancePct != null) candidates.push(resistancePct);
    if (visible.atr_alert && atrAlertPct != null) candidates.push(atrAlertPct);
    if (visible.atr_stop  && atrStopPct  != null) candidates.push(atrStopPct);
    if (candidates.length === 0) return ["auto", "auto"];
    const lo = Math.min(...candidates);
    const hi = Math.max(...candidates);
    const range = Math.max(hi - lo, 0.5);
    const pad = Math.max(range * 0.08, 0.5);
    return [Math.floor((lo - pad) * 100) / 100, Math.ceil((hi + pad) * 100) / 100];
  }, [chartData, visible, allKeys, supportPct, resistancePct, atrStopPct, atrAlertPct]);

  // Line definitions — benchmarks first, then holdings.
  const lines = useMemo(() => {
    const benches = CORE_BENCHMARKS.map(b => ({
      ...b,
      label: b.key === "etf" ? `${etfTicker} (ETF)` : b.label,
    }));
    const heldLines = trackedHoldings.map(h => ({
      key: h.symbol,
      label: h.symbol,
      color: h.color,
      strokeWidth: 1.6,
      kind: "holding",
      weight_pct: h.weight_pct,
      name: h.name,
    }));
    return [...benches, ...heldLines];
  }, [etfTicker, trackedHoldings]);

  // Relative-Strength Ranker
  const { leaders, laggards, rsByHolding } = useMemo(() => {
    if (etfPct == null) return { leaders: [], laggards: [], rsByHolding: {} };
    const rows = trackedHoldings
      .map(h => {
        const p = performance[h.symbol]?.pct;
        if (p == null) return null;
        return {
          symbol: h.symbol, color: h.color, name: h.name,
          weight_pct: h.weight_pct, pct: p,
          rs: Math.round((p - etfPct) * 100) / 100,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.rs - a.rs);
    const rsMap = {};
    rows.forEach(r => { rsMap[r.symbol] = r.rs; });
    return {
      leaders: rows.filter(r => r.rs > 0).slice(0, 8),
      laggards: rows.filter(r => r.rs < 0).reverse().slice(0, 8),
      rsByHolding: rsMap,
    };
  }, [performance, trackedHoldings, etfPct]);

  // Quick-action helpers — apply to chart visibility and lazy-fetch any
  // holdings we haven't loaded yet so the chart can actually render them.
  const showOnlyHoldings = useCallback((symbols, opts = {}) => {
    const keep = new Set(symbols);
    setVisible(v => {
      const next = {
        ...v,
        etf: true,
        spy: opts.keepSpy !== false,
      };
      trackedHoldings.forEach(h => { next[h.symbol] = keep.has(h.symbol); });
      return next;
    });
    symbols.forEach(sym => {
      if (!seriesData[sym]) fetchHolding(sym, timeframe);
    });
  }, [trackedHoldings, seriesData, fetchHolding, timeframe]);

  const handleShowLeaders   = () => showOnlyHoldings(leaders.slice(0, 5).map(l => l.symbol));
  const handleShowLaggards  = () => showOnlyHoldings(laggards.slice(0, 5).map(l => l.symbol));
  const handleShowTopMovers = () => {
    const ranked = [...leaders, ...laggards]
      .sort((a, b) => Math.abs(b.rs) - Math.abs(a.rs))
      .slice(0, 5).map(r => r.symbol);
    showOnlyHoldings(ranked);
  };
  const handleResetTop5 = () => showOnlyHoldings(trackedHoldings.slice(0, 5).map(h => h.symbol));

  // -------- Tooltip --------
  const fmtVol = (v) => {
    if (v == null) return "—";
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
    return `${v}`;
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const rawVol = payload[0]?.payload?.volume_raw;
    const rawAtr = payload[0]?.payload?.atr_raw;
    const sorted = [...payload].sort((a, b) => {
      const order = { [`${etfTicker} (ETF)`]: 0, "SPY · S&P 500": 1, "QQQ · Nasdaq": 2, "DIA · Dow": 3 };
      const oa = order[a.name] ?? 100, ob = order[b.name] ?? 100;
      if (oa !== ob) return oa - ob;
      return (b.value ?? -999) - (a.value ?? -999);
    });
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-2 shadow-xl text-[11px] max-h-96 overflow-auto">
        <p className="text-slate-300 mb-1.5 font-mono font-bold">{label}</p>
        <div className="space-y-0.5">
          {sorted.map((entry, i) => {
            const isPctAxis = ["RSI", "MFI", "VIX"].includes(entry.name);
            const isVolume  = entry.name === "Volume";
            const isAtr     = entry.name === "ATR(14)";
            let val;
            if (isVolume)      val = fmtVol(rawVol);
            else if (isAtr)    val = rawAtr != null ? `$${rawAtr.toFixed(2)}` : "—";
            else if (isPctAxis) val = entry.value?.toFixed(1);
            else val = fmtPct(entry.value);
            return (
              <div key={i} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-slate-200 truncate">{entry.name}:</span>
                <span className="ml-auto font-bold font-mono" style={{ color: entry.color }}>{val}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!etfTicker || trackedHoldings.length === 0) return null;

  // Convenience flags
  const heldKeys = trackedHoldings.map(h => h.symbol);
  const onCount  = heldKeys.filter(k => visible[k]).length;
  const allHoldingsOn = onCount === heldKeys.length;
  const loadedHoldings = trackedHoldings.filter(h => seriesData[h.symbol]).length;

  return (
    <div
      className="rounded-lg border border-emerald-900/40 bg-gradient-to-br from-slate-900/60 to-slate-900/40 p-3 space-y-3"
      data-testid="etf-holdings-benchmark-chart"
    >
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <BarChart3 className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-semibold text-white">Holdings vs ETF · Multi-Benchmark</span>
        <span className="font-mono text-cyan-300 font-bold">{etfTicker}</span>
        <span className="text-[10px] text-slate-300 font-normal">
          · {timeframe.toUpperCase()} · Y-axis = % change from start · {trackedHoldings.length} holdings · {loadedHoldings} loaded
        </span>
        <button
          onClick={loadInitial} disabled={loading}
          data-testid="ehbc-refresh"
          className="ml-auto text-[11px] px-2 py-1 rounded border border-slate-500 hover:bg-slate-800 text-slate-100 flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Benchmark toggles */}
      <div className="space-y-1.5" data-testid="ehbc-toggles">
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
            Benchmarks
          </span>
          {lines.filter(l => l.kind !== "holding").map(l => {
            const on = visible[l.key];
            return (
              <button
                key={l.key}
                onClick={() => toggle(l.key)}
                data-testid={`ehbc-toggle-${l.key}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${on ? "bg-slate-800 border-slate-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={on ? { boxShadow: `inset 0 0 0 2px ${l.color}` } : {}}
              >
                {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
                {l.label}
              </button>
            );
          })}
        </div>

        {/* Core overlays — regression + VWAP */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
            ETF Overlays
          </span>
          {[
            { key: "regression", label: "Regression", color: "#f97316" },
            { key: "vwap",       label: "VWAP",       color: "#ee82ee" },
          ].map(o => {
            const on = visible[o.key];
            return (
              <button
                key={o.key}
                onClick={() => toggle(o.key)}
                data-testid={`ehbc-toggle-${o.key}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${on ? "bg-slate-800 border-slate-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={on ? { boxShadow: `inset 0 0 0 2px ${o.color}` } : {}}
              >
                {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: o.color }} />
                {o.label}
              </button>
            );
          })}
        </div>

        {/* Moving Averages */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
            Moving Avgs ({etfTicker})
          </span>
          {MA_CONFIG.map(m => {
            const on = visible[m.key];
            const insufficient = chartData.length > 0 && chartData.length < m.period;
            return (
              <button
                key={m.key}
                onClick={() => !insufficient && toggle(m.key)}
                disabled={insufficient}
                title={insufficient ? `Need ${m.period} bars, have ${chartData.length}` : ""}
                data-testid={`ehbc-toggle-${m.key}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${insufficient ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                    : on ? "bg-slate-800 border-slate-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={on && !insufficient ? { boxShadow: `inset 0 0 0 2px ${m.color}` } : {}}
              >
                {insufficient ? <EyeOff className="w-3 h-3" /> : on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Right-axis indicators + S/R + ATR levels */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
            Indicators
          </span>
          {RIGHT_AXIS_INDICATORS.map(ind => {
            const on = visible[ind.key];
            return (
              <button
                key={ind.key}
                onClick={() => toggle(ind.key)}
                data-testid={`ehbc-toggle-${ind.key}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${on ? "bg-slate-800 border-slate-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={on ? { boxShadow: `inset 0 0 0 2px ${ind.color}` } : {}}
              >
                {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ind.color }} />
                {ind.label}
              </button>
            );
          })}
          <button
            onClick={() => toggle("zones")}
            data-testid="ehbc-toggle-zones"
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
              ${visible.zones ? "bg-slate-800 border-slate-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
            style={visible.zones ? { boxShadow: "inset 0 0 0 2px #10b981" } : {}}
          >
            {visible.zones ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            <span className="w-2 h-2 rounded-full" style={{ background: "linear-gradient(to bottom, #ef4444 50%, #10b981 50%)" }} />
            Support / Resistance
          </button>
          <button
            onClick={() => toggle("atr_alert")}
            disabled={atrAlertPct == null}
            data-testid="ehbc-toggle-atr-alert"
            title={atrAlertPct == null ? "Need ≥3 bars to compute ATR" : `1× ATR alert @ ${fmtPct(atrAlertPct)}`}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
              ${atrAlertPct == null ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                : visible.atr_alert ? "bg-slate-800 border-slate-500 text-white"
                : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
            style={visible.atr_alert && atrAlertPct != null ? { boxShadow: "inset 0 0 0 2px #facc15" } : {}}
          >
            {visible.atr_alert && atrAlertPct != null ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#facc15" }} />
            1× ATR alert
          </button>
          <button
            onClick={() => toggle("atr_stop")}
            disabled={atrStopPct == null}
            data-testid="ehbc-toggle-atr-stop"
            title={atrStopPct == null ? "Need ≥3 bars to compute ATR" : `2× ATR stop @ ${fmtPct(atrStopPct)}`}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
              ${atrStopPct == null ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                : visible.atr_stop ? "bg-slate-800 border-slate-500 text-white"
                : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
            style={visible.atr_stop && atrStopPct != null ? { boxShadow: "inset 0 0 0 2px #f43f5e" } : {}}
          >
            {visible.atr_stop && atrStopPct != null ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#f43f5e" }} />
            2× ATR stop
          </button>
          <button
            onClick={() => toggle("atr_zone")}
            disabled={atrAlertPct == null || atrStopPct == null}
            data-testid="ehbc-toggle-atr-zone"
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
              ${(atrAlertPct == null || atrStopPct == null) ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                : visible.atr_zone ? "bg-slate-800 border-slate-500 text-white"
                : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
            style={visible.atr_zone && atrAlertPct != null && atrStopPct != null ? { boxShadow: "inset 0 0 0 2px #f59e0b" } : {}}
          >
            {visible.atr_zone && atrAlertPct != null && atrStopPct != null ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#f59e0b" }} />
            Warning zone
          </button>
        </div>

        {/* Holdings toggle row — full universe with lazy-fetch loading state */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
            Holdings ({trackedHoldings.length})
          </span>
          <button
            onClick={allHoldingsOn ? handleHideAllHoldings : handleShowAllHoldings}
            data-testid="ehbc-toggle-all-holdings"
            className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold
              ${allHoldingsOn
                ? "border-rose-600 text-rose-200 hover:bg-rose-950"
                : "border-emerald-600 text-emerald-200 hover:bg-emerald-950"}`}
          >
            {allHoldingsOn ? "Hide all" : `Show all ${trackedHoldings.length}`}
          </button>
          <span className="text-[9px] text-slate-500 font-mono">({onCount} on)</span>
          {trackedHoldings.map(h => {
            const on = visible[h.symbol];
            const isPending = pendingSymbols.has(h.symbol);
            const rs = rsByHolding[h.symbol];
            const rsClass = rs == null ? "text-slate-500"
              : rs > 0 ? "text-emerald-300"
              : rs < 0 ? "text-rose-300"
              : "text-slate-400";
            return (
              <button
                key={h.symbol}
                onClick={() => toggleHolding(h.symbol)}
                data-testid={`ehbc-toggle-${h.symbol}`}
                title={`${h.name || h.symbol} · RS vs ETF: ${rs == null ? "—" : (rs >= 0 ? "+" : "") + rs.toFixed(2) + "%"}`}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold border transition-colors
                  ${on ? "bg-slate-800 border-slate-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={on ? { boxShadow: `inset 0 0 0 2px ${h.color}` } : {}}
              >
                {isPending ? <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />
                  : on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: h.color }} />
                <span className="font-mono">{h.symbol}</span>
                {h.weight_pct != null && (
                  <span className="text-[9px] text-slate-400 font-mono">{h.weight_pct.toFixed(1)}%</span>
                )}
                {rs != null && (
                  <span className={`text-[9px] font-mono font-bold ${rsClass}`}>
                    {rs >= 0 ? "+" : ""}{rs.toFixed(1)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Relative-Strength Ranker */}
      {etfPct != null && (leaders.length > 0 || laggards.length > 0) && (
        <div className="rounded-md border border-slate-800 bg-slate-900/50 p-2.5 space-y-2" data-testid="ehbc-rs-ranker">
          <div className="flex items-center gap-2 flex-wrap">
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] uppercase tracking-wider text-slate-200 font-bold">
              Relative Strength vs {etfTicker}
            </span>
            <span className="text-[10px] text-slate-400 font-mono">
              ({etfTicker} {etfPct >= 0 ? "+" : ""}{etfPct.toFixed(2)}% · {timeframe.toUpperCase()})
            </span>
            <div className="ml-auto flex flex-wrap gap-1.5">
              <button onClick={handleShowLeaders} disabled={leaders.length === 0}
                data-testid="ehbc-rs-show-leaders"
                className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-700 text-emerald-200 hover:bg-emerald-950 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Show leaders
              </button>
              <button onClick={handleShowLaggards} disabled={laggards.length === 0}
                data-testid="ehbc-rs-show-laggards"
                className="text-[10px] px-2 py-0.5 rounded-full border border-rose-700 text-rose-200 hover:bg-rose-950 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> Show laggards
              </button>
              <button onClick={handleShowTopMovers}
                data-testid="ehbc-rs-show-movers"
                className="text-[10px] px-2 py-0.5 rounded-full border border-amber-700 text-amber-200 hover:bg-amber-950 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Top movers
              </button>
              <button onClick={handleResetTop5}
                data-testid="ehbc-rs-reset"
                className="text-[10px] px-2 py-0.5 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800">
                Reset
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="rounded border border-emerald-900/40 bg-emerald-950/20 p-1.5" data-testid="ehbc-rs-leaders">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">
                <TrendingUp className="w-3 h-3" /> Leaders · Outperforming {etfTicker}
              </div>
              {leaders.length === 0 ? (
                <div className="text-[10px] text-slate-500 italic px-1 py-0.5">No constituents are beating the ETF this window.</div>
              ) : (
                <div className="space-y-0.5">
                  {leaders.map((l, idx) => (
                    <button key={l.symbol}
                      onClick={() => toggleHolding(l.symbol)}
                      data-testid={`ehbc-rs-leader-${l.symbol}`}
                      title={`${l.name || l.symbol}`}
                      className={`w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono hover:bg-emerald-900/30 transition-colors
                        ${visible[l.symbol] ? "ring-1 ring-emerald-500/50" : ""}`}>
                      <span className="text-slate-500 w-4 text-right">{idx + 1}.</span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                      <span className="text-cyan-300 font-bold w-12 text-left">{l.symbol}</span>
                      <span className="text-slate-400 truncate flex-1 text-left text-[9px]">{l.name || ""}</span>
                      <span className="text-slate-300">{l.pct >= 0 ? "+" : ""}{l.pct.toFixed(1)}%</span>
                      <span className="text-emerald-300 font-bold w-14 text-right">+{l.rs.toFixed(1)} RS</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded border border-rose-900/40 bg-rose-950/20 p-1.5" data-testid="ehbc-rs-laggards">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-300 font-bold mb-1">
                <TrendingDown className="w-3 h-3" /> Laggards · Underperforming {etfTicker}
              </div>
              {laggards.length === 0 ? (
                <div className="text-[10px] text-slate-500 italic px-1 py-0.5">No constituents are lagging the ETF this window.</div>
              ) : (
                <div className="space-y-0.5">
                  {laggards.map((l, idx) => (
                    <button key={l.symbol}
                      onClick={() => toggleHolding(l.symbol)}
                      data-testid={`ehbc-rs-laggard-${l.symbol}`}
                      title={`${l.name || l.symbol}`}
                      className={`w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono hover:bg-rose-900/30 transition-colors
                        ${visible[l.symbol] ? "ring-1 ring-rose-500/50" : ""}`}>
                      <span className="text-slate-500 w-4 text-right">{idx + 1}.</span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                      <span className="text-cyan-300 font-bold w-12 text-left">{l.symbol}</span>
                      <span className="text-slate-400 truncate flex-1 text-left text-[9px]">{l.name || ""}</span>
                      <span className="text-slate-300">{l.pct >= 0 ? "+" : ""}{l.pct.toFixed(1)}%</span>
                      <span className="text-rose-300 font-bold w-14 text-right">{l.rs.toFixed(1)} RS</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      {loading && chartData.length === 0 ? (
        <div className="h-[560px] bg-slate-800/60 animate-pulse rounded" data-testid="ehbc-skel" />
      ) : chartData.length === 0 ? (
        <div className="h-[560px] flex items-center justify-center text-slate-300 text-sm">
          No comparison data available.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={560}>
          <LineChart data={chartData} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="time" stroke="#cbd5e1" tick={{ fill: '#cbd5e1', fontSize: 9 }} />
            <YAxis yAxisId="pct" domain={yDomain} stroke="#cbd5e1"
                   tick={{ fill: '#cbd5e1', fontSize: 9 }}
                   tickFormatter={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`} />
            <YAxis yAxisId="ind" orientation="right" domain={[0, 100]} stroke="#94a3b8"
                   tick={{ fill: '#94a3b8', fontSize: 9 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: '6px', fontSize: '10px' }} iconType="circle" />

            {/* Zero baseline — clearly marks "no change" on the %-axis */}
            <ReferenceLine yAxisId="pct" y={0} stroke="#64748b" strokeDasharray="2 4" />

            {/* Support / Resistance bands */}
            {visible.zones && supportPct != null && resistancePct != null && (
              <>
                <ReferenceLine yAxisId="pct" y={supportPct} stroke="#10b981" strokeDasharray="4 2"
                  label={{ value: `Support ${fmtPct(supportPct)}`, position: "insideBottomLeft", fill: "#34d399", fontSize: 10, fontWeight: 600 }} />
                <ReferenceLine yAxisId="pct" y={resistancePct} stroke="#ef4444" strokeDasharray="4 2"
                  label={{ value: `Resistance ${fmtPct(resistancePct)}`, position: "insideTopLeft", fill: "#f87171", fontSize: 10, fontWeight: 600 }} />
              </>
            )}

            {/* ATR alert / stop lines */}
            {visible.atr_alert && atrAlertPct != null && (
              <ReferenceLine yAxisId="pct" y={atrAlertPct}
                stroke="#facc15" strokeWidth={2} strokeDasharray="6 3"
                label={{ value: `1× ATR alert ${fmtPct(atrAlertPct)}`, position: "insideTopRight", fill: "#fde047", fontSize: 10, fontWeight: 600 }} />
            )}
            {visible.atr_stop && atrStopPct != null && (
              <ReferenceLine yAxisId="pct" y={atrStopPct}
                stroke="#f43f5e" strokeWidth={2} strokeDasharray="6 3"
                label={{ value: `2× ATR stop ${fmtPct(atrStopPct)} (ATR ${lastAtr?.toFixed(2)})`, position: "insideTopRight", fill: "#fda4af", fontSize: 10, fontWeight: 600 }} />
            )}
            {visible.atr_zone && atrAlertPct != null && atrStopPct != null && (
              <ReferenceArea yAxisId="pct" y1={atrStopPct} y2={atrAlertPct}
                fill="#f59e0b" fillOpacity={0.18} stroke="#f59e0b" strokeOpacity={0.35} strokeDasharray="3 3" />
            )}

            {/* Holding + benchmark lines (% axis) */}
            {lines.map(l => visible[l.key] && (
              <Line key={l.key} yAxisId="pct" type="monotone" dataKey={l.key} name={l.label}
                stroke={l.color} strokeWidth={l.strokeWidth}
                strokeDasharray={l.kind === "bench" && l.key !== "etf" ? "4 2" : undefined}
                dot={false} connectNulls isAnimationActive={false} />
            ))}

            {/* Overlay: regression + VWAP (% axis) */}
            {visible.regression && (
              <Line yAxisId="pct" type="monotone" dataKey="regression" name="Regression"
                stroke="#f97316" strokeWidth={2.2} strokeDasharray="5 5"
                dot={false} connectNulls isAnimationActive={false} />
            )}
            {visible.vwap && (
              <Line yAxisId="pct" type="monotone" dataKey="vwap" name="VWAP (fair value)"
                stroke="#ee82ee" strokeWidth={2.2} strokeDasharray="4 3"
                dot={false} connectNulls isAnimationActive={false} />
            )}

            {/* Moving averages (% axis) */}
            {MA_CONFIG.map(m => visible[m.key] && (
              <Line key={m.key} yAxisId="pct" type="monotone" dataKey={m.key} name={m.label}
                stroke={m.color} strokeWidth={2.0}
                strokeDasharray={m.kind === "sma" ? "4 2" : undefined}
                dot={false} connectNulls isAnimationActive={false} />
            ))}

            {/* Right-axis indicators (0-100) */}
            {RIGHT_AXIS_INDICATORS.map(ind => visible[ind.key] && (
              <Line key={ind.key} yAxisId="ind" type="monotone" dataKey={ind.key} name={ind.label}
                stroke={ind.color} strokeWidth={1.8} strokeDasharray={ind.dasharray}
                dot={false} connectNulls isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Performance grid — only visible series, sorted by % gain desc */}
      {Object.keys(performance).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5" data-testid="ehbc-perf">
          {lines
            .filter(l => visible[l.key] && performance[l.key]?.pct != null)
            .sort((a, b) => (performance[b.key].pct ?? 0) - (performance[a.key].pct ?? 0))
            .map(l => {
              const p = performance[l.key];
              return (
                <div key={l.key} className="rounded border border-slate-800 bg-slate-900/60 px-2 py-1.5" data-testid={`ehbc-perf-${l.key}`}>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="text-[10px] font-mono font-bold truncate" style={{ color: l.color }}>{l.label}</span>
                  </div>
                  <div className={`font-mono text-sm font-bold ${pctClass(p.pct)}`}>{fmtPct(p.pct)}</div>
                  {p.start != null && p.end != null && (
                    <div className="text-[9px] font-mono text-slate-500">
                      ${p.start.toFixed(2)} → ${p.end.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {regressionStats && visible.regression && (
        <div className="text-[11px] text-slate-200 font-mono">
          ETF Regression direction:{" "}
          <span className={`font-bold ${regressionStats.slope >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {regressionStats.slope >= 0 ? "UP" : "DOWN"}
          </span>
          <span className="ml-2 text-slate-100">
            fit {fmtPct(regressionStats.startPct)} → {fmtPct(regressionStats.endPct)}
          </span>
          {atrPeriod && (
            <span className="ml-2 text-slate-400">· ATR period {atrPeriod}</span>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 leading-snug">
        Every series is plotted as % change from the start of the window so {etfTicker}, its constituents,
        SPY/QQQ/DIA, and all ETF-derived indicators (EMAs, SMAs, VWAP, ATR, S/R) sit on one comparable Y-axis.
        The Ranker auto-sorts holdings by RS (their % return − the ETF&apos;s) so leaders and laggards surface without manual toggling.
      </p>
    </div>
  );
}
